import {
  isAppError,
  toAppError,
  type AppError,
  type LoginRequest,
  type Permission,
  type SessionUser,
} from '@kdk/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api/api';
import { clearSession, readSession, writeSession } from './session';
import { setUnauthorizedListener } from './unauthorized';

/** 未ログインになった理由。expired = セッション切れ、signed-out = 自分でログアウト。 */
export type AnonymousReason = 'none' | 'expired' | 'signed-out';

type State =
  | { status: 'loading' }
  | { status: 'anonymous'; reason: AnonymousReason }
  | { status: 'error'; error: AppError }
  | { status: 'authenticated'; user: SessionUser };

export type Auth = {
  status: State['status'];
  user: SessionUser | null;
  error: AppError | null;
  reason: AnonymousReason;
  retry: () => void;
  can: (permission: Permission) => boolean;
  login: (input: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<Auth | null>(null);

export const useAuth = (): Auth => {
  const auth = useContext(AuthContext);
  if (auth === null) throw new Error('useAuth は AuthProvider の内側で使ってください');
  return auth;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>(() =>
    readSession() === null ? { status: 'anonymous', reason: 'none' } : { status: 'loading' },
  );
  const [attempt, setAttempt] = useState(0);

  // token = 失敗したリクエストが送ったトークン。現在のセッションのものでなければ(再ログイン後に
  // 遅れて届いた古い 401)無視する。冪等: 同じトークンの 401 が複数回来ても結果は同じ。
  const expire = useCallback(
    (token: string) => {
      if (readSession()?.token !== token) return;
      clearSession();
      queryClient.clear();
      setState({ status: 'anonymous', reason: 'expired' });
    },
    [queryClient],
  );

  // API クライアントが「トークンを付けたのに 401」を見つけたら、ここで未ログインに戻す
  useEffect(() => {
    setUnauthorizedListener(expire);
    return () => {
      setUnauthorizedListener(null);
    };
  }, [expire]);

  // 保存されたセッションがあれば、サーバーで有効か確認し、権限はサーバーの答えを使う。
  // 401 だけを「未ログイン」とみなし、ネットワーク/500 は再試行できるエラー状態にする。
  useEffect(() => {
    const stored = readSession();
    if (stored === null) return;
    let cancelled = false;
    const isCurrent = (): boolean => !cancelled && readSession()?.token === stored.token;
    api.getMe().then(
      (user) => {
        if (!isCurrent()) return;
        writeSession({ token: stored.token, user });
        setState({ status: 'authenticated', user });
      },
      (error: unknown) => {
        if (!isCurrent()) return;
        if (isAppError(error) && error.status === 401) {
          expire(stored.token);
          return;
        }
        setState({ status: 'error', error: toAppError(error) });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [attempt, expire]);

  const retry = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  const login = useCallback(
    async (input: LoginRequest): Promise<void> => {
      const { token, user } = await api.login(input);
      writeSession({ token, user });
      queryClient.clear();
      setState({ status: 'authenticated', user });
    },
    [queryClient],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      // サーバー側で失効できなくても、この端末のセッションは必ず消す
    }
    clearSession();
    queryClient.clear();
    setState({ status: 'anonymous', reason: 'signed-out' });
  }, [queryClient]);

  const user = state.status === 'authenticated' ? state.user : null;
  const can = useCallback(
    (permission: Permission): boolean => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo(
    (): Auth => ({
      status: state.status,
      user,
      error: state.status === 'error' ? state.error : null,
      reason: state.status === 'anonymous' ? state.reason : 'none',
      retry,
      can,
      login,
      logout,
    }),
    [state, user, retry, can, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
